import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useSelector } from "react-redux";
import { ArrowUpDown, Eye, MoveDown, MoveUp } from "lucide-react";
import { useGetRatingsByVendorMutation } from "../../api/api";
import { Input } from "../../@/components/ui/input";
import {
  Table,
  TableHeader,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
} from "../../@/components/ui/table";
import SkeletonTableLoading from "../../skeletonLoading/SkeletonTableLoading";
import useDebounce, {
  formatDate,
  formatDateTime,
  tokenDecoder,
  IsServiceTypeVendor,
  isMobileScreen,
} from "../../lib/utils";
import { ReviewDialog } from "./ReviewDialog";
import ShowStarRating from "./ShowStarRating";
import Pagination from "../../components/Pagination";
import { FiSearch } from "react-icons/fi";
import SingleDatePicker from "../../components/SingleDatePicker";
import { Checkbox } from "../../@/components/ui/checkbox";
import { Button } from "../../@/components/ui/button";
import { useToast } from "../../@/components/ui/use-toast";
import { find } from "@reduxjs/toolkit/dist/utils";
import ReviewSelectionSave from "./ReviewSelectionSave";
import { MdModeEdit } from "react-icons/md";

declare module "@tanstack/table-core" {
  interface ColumnMeta<TData extends unknown, TValue> {
    isTooltip?: boolean;
  }
}

interface Data {
  productId: number;
  productName: string;
  productImage: string;
  customerName: string;
  productRatingId: number;
  review: string;
  createdAt: string;
  vendorId: number;
  vendorName: string;
  ratingValue: number;
  vendorReply: string | null;
  vendorReplyDate: string | null;
  orderDate: string;
}

const ReviewListView: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearchQuery = useDebounce(searchTerm, 500);
  const token = useSelector((state: any) => state.auth.token);
  const decodedToken = tokenDecoder(token);
  const vendorId = decodedToken.vendorId!;
  const userRole = decodedToken.role;
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, _] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [data, setData] = useState<any>([]);
  const [reviewslectionproductdata, setReviewslectionproductdata] =
    useState<any>([]);
  const { toast } = useToast();

  const [
    getReviews,
    { error, isLoading: getReviewsLoading, ...getRatingsQuery },
  ] = useGetRatingsByVendorMutation();
  const [sortingOrder, setSortingOrder] = useState<"asc" | "desc" | undefined>(
    "desc"
  );
  const [sortingColumn, setSortingColumn] = useState<string | undefined>(
    "createdAt"
  );

  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [uniqueRatingIdsToSave, setUniqueRatingIdsToSave] = useState<number[]>(
    []
  );

  const [showReviewSelectionSave, setShowReviewSelectionSave] = useState(false);

  const [selectedProductId, setSelectedProductId] = useState<number | null>(
    null
  );

  const [isReviewDialogOpen, setIsReviewDialogOpen] = useState<boolean>(false);
  const [isReadOnly, setIsReadOnly] = useState<boolean>(false);

  const [persistentSelections, setPersistentSelections] = useState<{
    [key: string]: boolean;
  }>({});

  const handleSorting = (column: string) => {
    if (sortingColumn === column) {
      setSortingOrder(sortingOrder === "asc" ? "desc" : "asc");
    } else {
      setSortingColumn(column);
      setSortingOrder("asc");
    }
    setPageNumber(1);
  };

  const fetchData = useCallback(async () => {
    try {
      const response: any = await getReviews({
        getRatingsByVendorRequestDto: {
          vendorId: decodedToken.role === "ADMIN" ? undefined : vendorId,
          searchQuery:
            debouncedSearchQuery !== "" ? debouncedSearchQuery : undefined,
          pageNumber,
          pageSize,
          getTotalCount: true,
          sortingOrder,
          sortingColumn,
          startDate: startDate
            ? formatDateTime(startDate, "YYYY-MM-DDTHH:mm:ssZ")
            : undefined,
          endDate: endDate
            ? formatDateTime(endDate, "YYYY-MM-DDTHH:mm:ssZ")
            : undefined,
        },
      }).unwrap();
      setData(response?.data?.products || []);
      setTotalCount(response.data.totalCount || 0);
    } catch (err) {
      console.error("Failed to get products: ", err);
    }
  }, [
    getReviews,
    debouncedSearchQuery,
    pageNumber,
    vendorId,
    sortingColumn,
    sortingOrder,
    startDate,
    endDate,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData, pageNumber]);

  useEffect(() => {
    const hasSelectedRows = Object.values(persistentSelections).some(
      (value) => value
    );
    if (!hasSelectedRows) {
      setSelectedProductId(null);
    }
  }, [persistentSelections]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (pageNumber !== 1) {
      setPageNumber(1);
    }
    setSearchTerm(event.target.value);
  };

  const handlePageChange = (newPageNumber: number) => {
    if (newPageNumber < 0 || newPageNumber > Math.ceil(totalCount / pageSize)) {
      return;
    }
    setPageNumber(newPageNumber);
  };

  const handleShowSelectedReviews = () => {
    const selectedIds = Object.entries(persistentSelections)
      .filter(([_, isSelected]) => isSelected)
      .map(([id]) => parseInt(id));

    if (selectedIds.length === 0) {
      toast({
        title: "No product selected",
        description: "please select a Review to save.",
      });
      return;
    } else if (selectedIds.length > 3) {
      toast({
        title: "Too many products selected",
        description: "please select a maximum of 3 Reviews to save.",
      });
      return;
    }

    const allSelectedProducts = selectedIds
      .map((id) => data.find((item: any) => item.productRatingId === id))
      .filter(Boolean);

    const firstProductId = allSelectedProducts[0]?.productId;
    const allSameProduct = allSelectedProducts.every(
      (item: any) => item.productId === firstProductId
    );

    if (!allSameProduct) {
      toast({
        title: "Product Mismatch",
        description: "Please select the same product for saving reviews.",
      });
      return;
    }

    const lowRatingCount = allSelectedProducts.filter(
      (item: any) => item.ratingValue <= 2
    ).length;
    const highRatingCount = allSelectedProducts.filter(
      (item: any) => item.ratingValue >= 3
    ).length;

    if (lowRatingCount > 1) {
      toast({
        title: "Invalid rating selection",
        description: "You can only select one review with rating 1-2.",
      });
      return;
    }

    if (highRatingCount > 2) {
      toast({
        title: "Invalid rating selection",
        description: "You can only select two reviews with ratings 3-5.",
      });
      return;
    }

    setReviewslectionproductdata(allSelectedProducts);
    setUniqueRatingIdsToSave(selectedIds);
    setShowReviewSelectionSave(true);
    console.log("allSelectedProducts: ", allSelectedProducts);
  };

  const handleOpenReviewDialog = (
    productRatingId: number,
    readOnly: boolean = false
  ) => {
    setSelectedProductId(productRatingId);
    setIsReadOnly(readOnly);
    setIsReviewDialogOpen(true);
  };

  const getSelectionCount = useCallback(() => {
    return Object.values(persistentSelections).filter(Boolean).length;
  }, [persistentSelections]);

  const columns = useMemo<ColumnDef<Data>[]>(() => {
    const baseColumns: ColumnDef<Data>[] = [
      {
        id: "select",
        cell: ({ row }) => {
          const isDisabled =
            selectedProductId !== null &&
            selectedProductId !== row.original.productId;

          return (
            <Checkbox
              checked={
                persistentSelections[row.original.productRatingId] || false
              }
              onCheckedChange={(value) => {
                if (
                  value &&
                  selectedProductId !== null &&
                  selectedProductId !== row.original.productId
                ) {
                  setPersistentSelections({});
                  setSelectedProductId(null);

                  setTimeout(() => {
                    setSelectedProductId(row.original.productId);
                    setPersistentSelections({
                      [row.original.productRatingId]: true,
                    });
                  }, 0);
                  return;
                }

                if (selectedProductId === null && value) {
                  setSelectedProductId(row.original.productId);
                }

                setPersistentSelections((prev) => ({
                  ...prev,
                  [row.original.productRatingId]: value === true,
                }));

                if (!value) {
                  const newSelection = {
                    ...persistentSelections,
                    [row.original.productRatingId]: false,
                  };
                  if (!Object.values(newSelection).some((v) => v)) {
                    setSelectedProductId(null);
                  }
                }
              }}
              disabled={isDisabled}
              aria-label="Select row"
            />
          );
        },
        size: 20,
        meta: {
          isTooltip: false,
        },
      },
      {
        accessorKey: "reviews",
        header: ({ column }) => (
          <div
            onClick={() => handleSorting("productName")}
            className="cursor-pointer flex gap-1 items-center"
          >
            Reviews{" "}
            {sortingColumn === "productName" ? (
              sortingOrder === "asc" ? (
                <MoveUp size={14} />
              ) : (
                <MoveDown size={14} />
              )
            ) : (
              <ArrowUpDown size={14} />
            )}
          </div>
        ),
        size: 250,
        cell: ({ row }) => (
          <div className="flex items-center overflow-hidden text-ellipsis">
            <img
              src={row.original.productImage}
              alt="Product"
              className="w-12 h-12 mr-4 border rounded"
            />
            <div>
              <div className="text-sm font-semibold text-customBlackColor">
                {isMobileScreen
                  ? `${row.original.productName.slice(0, 22)}...`
                  : row.original.productName}
              </div>
              <div className="text-customBlackColor overflow-hidden text-ellipsis text-nowrap lg:text-wrap text-sm flex items-center min-w-48">
                {row.original.review.length < 65
                  ? row.original.review
                  : isMobileScreen
                  ? `${row.original.review.slice(0, 22)}...`
                  : `${row.original.review.slice(0, 65)}...`}
              </div>
            </div>
          </div>
        ),
      },
      {
        accessorKey: "createdAt",
        header: ({ column }) => (
          <div
            onClick={() => handleSorting("createdAt")}
            className="cursor-pointer flex gap-1 items-center text-nowrap"
          >
            Date{" "}
            {sortingColumn === "createdAt" ? (
              sortingOrder === "asc" ? (
                <MoveUp size={14} />
              ) : (
                <MoveDown size={14} />
              )
            ) : (
              <ArrowUpDown size={14} />
            )}
          </div>
        ),
        size: 100,
        cell: ({ cell }) => (
          <span className="text-nowrap text-[#797C87]">
            {formatDate(cell.getValue() as string, "dd MMM yyyy")}
          </span>
        ),
      },
      {
        accessorKey: "customerName",
        header: ({ column }) => (
          <div
            onClick={() => handleSorting("customerName")}
            className="cursor-pointer flex gap-1 items-center"
          >
            Customer{" "}
            {sortingColumn === "customerName" ? (
              sortingOrder === "asc" ? (
                <MoveUp size={14} />
              ) : (
                <MoveDown size={14} />
              )
            ) : (
              <ArrowUpDown size={14} />
            )}
          </div>
        ),
        size: 130,
        cell: ({ row }) => (
          <span className="text-nowrap text-[#6E7079]">
            {row.original?.customerName || "N/A"}
          </span>
        ),
      },
      {
        accessorKey: "ratingValue",
        header: ({ column }) => (
          <div
            onClick={() => handleSorting("ratingValue")}
            className="cursor-pointer flex gap-1 items-center"
          >
            Rating{" "}
            {sortingColumn === "ratingVlaue" ? (
              sortingOrder === "asc" ? (
                <MoveUp size={14} />
              ) : (
                <MoveDown size={14} />
              )
            ) : (
              <ArrowUpDown size={14} />
            )}
          </div>
        ),
        size: 130,
        cell: ({ cell }) => (
          <ShowStarRating rating={cell.getValue() as number} />
        ),
        meta: {
          isTooltip: false,
        },
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <div className="flex space-x-2 justify-center">
            <div className="flex items-center space-x-2">
              {userRole !== "ADMIN" && (
                <div>
                  <MdModeEdit
                    size={16}
                    className="cursor-pointer text-[#72767E] hover:text-black"
                    onClick={() =>
                      handleOpenReviewDialog(
                        row.original.productRatingId,
                        false
                      )
                    }
                  />
                </div>
              )}
              <div>
                <Eye
                  size={20}
                  className="cursor-pointer text-[#72767E] hover:text-black"
                  onClick={() =>
                    handleOpenReviewDialog(row.original.productRatingId, true)
                  }
                />
              </div>
            </div>
          </div>
        ),
        size: 80,
        meta: {
          isTooltip: false,
        },
      },
    ];

    if (decodedToken.role === "ADMIN") {
      baseColumns.splice(4, 0, {
        accessorKey: "vendorName",
        header: ({ column }) => (
          <div
            onClick={() => handleSorting("vendorName")}
            className="cursor-pointer flex gap-1 items-center"
          >
            Vendor{" "}
            {sortingColumn === "vendorName" ? (
              sortingOrder === "asc" ? (
                <MoveUp size={14} />
              ) : (
                <MoveDown size={14} />
              )
            ) : (
              <ArrowUpDown size={14} />
            )}
          </div>
        ),
        size: 130,
        cell: ({ row }) => (
          <span className="text-nowrap text-[#6E7079] overflow-hidden text-ellipsis">
            {row.original?.vendorName || "N/A"}
          </span>
        ),
      });
    }

    return baseColumns;
  }, [
    sortingColumn,
    sortingOrder,
    decodedToken.role,
    selectedProductId,
    persistentSelections,
  ]);

  const table = useReactTable({
    data: data.map((item: any) => ({
      productId: item.productId,
      productName: item.productName,
      rating: item.rating,
      productImage: item.productImage,
      customerName: item.customerName,
      productRatingId: item.productRatingId,
      review: item.review,
      createdAt: item.createdAt,
      vendorId: item.vendorId,
      vendorName: item.vendorName,
      ratingValue: item.ratingValue,
      vendorReply: item.vendorReply,
      vendorReplyDate: item.vendorReplyDate,
      orderDate: item.orderDate,
    })),
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    pageCount: Math.ceil(totalCount / pageSize),
    manualPagination: true,
    manualSorting: true,
    onRowSelectionChange: setPersistentSelections,
    state: {
      pagination: {
        pageIndex: pageNumber - 1,
        pageSize,
      },
      rowSelection: persistentSelections,
    },
  });

  const handleSaveSuccess = useCallback(() => {
    setPersistentSelections({});
    setSelectedProductId(null);
    setShowReviewSelectionSave(false);
  }, []);

  return (
    <div className="overflow-hidden h-full pl-4 pt-2 pr-4">
      <div className="flex justify-between items-center mb-4 flex-col sm:flex-row w-full gap-2">
        <div className="relative flex justify-center text-customGrayText items-center w-full sm:w-80">
          <FiSearch className="absolute left-2 text-lg" />
          <Input
            placeholder={`Search by ${
              IsServiceTypeVendor() ? "service" : "product"
            } or customer name…`}
            className="text-sm text-left h-9 pl-8 w-full"
            onChange={handleSearchChange}
          />
        </div>
        <div className="flex gap-2 flex-col sm:flex-row w-full sm:w-auto">
          <div className="w-full sm:w-auto">
            <button
              onClick={handleShowSelectedReviews}
              className="bg-customGreenColor text-white px-4 py-2 rounded-lg text-sm h-9 flex items-center gap-2 w-full justify-center"
            >
              Save Selected Reviews
              {getSelectionCount() > 0 && (
                <span className="bg-white text-customGreenColor rounded-full w-6 h-6 flex items-center justify-center font-medium">
                  {getSelectionCount()}
                </span>
              )}
            </button>
          </div>
          <div className="w-full sm:w-auto">
            <SingleDatePicker
              startDate={startDate}
              endDate={endDate}
              onStartDateSelect={setStartDate}
              onEndDateSelect={setEndDate}
            />
          </div>
        </div>
      </div>
      {getReviewsLoading ? (
        <div className="overflow-y-auto h-[calc(100vh-12rem)] mt-4">
          <div className="overflow-x-auto bg-white rounded-lg shadow h-full">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow
                    key={headerGroup.id}
                    className="bg-customGreenColor text-white z-10"
                  >
                    {headerGroup.headers.map((header) => (
                      <TableCell
                        key={header.id}
                        onClick={() => handleSorting(header.id)}
                        style={{
                          minWidth: header.column.columnDef.size,
                          maxWidth: header.column.columnDef.size,
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
            </Table>
            <SkeletonTableLoading />
          </div>
        </div>
      ) : (
        <div className="overflow-y-auto h-[calc(100vh-12rem)] cursor-default">
          <div className="overflow-x-auto bg-white rounded-lg shadow h-full">
            <Table className="">
              <TableHeader className="sticky top-0">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow
                    key={headerGroup.id}
                    className="bg-customGreenColor text-white"
                  >
                    {headerGroup.headers.map((header) => (
                      <TableHead
                        key={header.id}
                        onClick={() => handleSorting(header.id)}
                        style={{
                          minWidth: header.column.columnDef.size,
                          maxWidth: header.column.columnDef.size,
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          className="overflow-hidden text-ellipsis"
                          key={cell.id}
                          style={{
                            minWidth: cell.column.columnDef.size,
                            maxWidth: cell.column.columnDef.size,
                          }}
                          isTooltip={
                            cell.column.columnDef.meta?.isTooltip ?? true
                          }
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      No data available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
      {data.length > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2 pt-4 pb-4 overflow-hidden">
          <div className="mb-2 text-[#667085]">
            Page {table.getState().pagination.pageIndex + 1} of{" "}
            {Math.ceil(totalCount / pageSize)}
          </div>
          <Pagination
            totalCount={totalCount}
            pageSize={pageSize}
            currentPage={pageNumber}
            onPageChange={handlePageChange}
          />
        </div>
      )}
      {showReviewSelectionSave && (
        <ReviewSelectionSave
          productRatingIds={uniqueRatingIdsToSave}
          vendorId={reviewslectionproductdata[0].vendorId}
          productId={reviewslectionproductdata[0].productId}
          onSaveSuccess={handleSaveSuccess}
        />
      )}
      {isReviewDialogOpen && (
        <ReviewDialog
          reviewId={selectedProductId !== null ? selectedProductId : 0}
          userRole={decodedToken.role}
          isOpen={isReviewDialogOpen}
          onClose={() => setIsReviewDialogOpen(false)}
          isReadOnly={isReadOnly}
        />
      )}
    </div>
  );
};

export default ReviewListView;
